/**
 * Notification Bell Component
 *
 * Displays notification bell with unread count badge
 * Opens dropdown with recent notifications
 */

import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import {
  useUnreadNotificationCount,
  useNotifications,
  useMarkNotificationAsRead,
  useMarkAllNotificationsAsRead,
} from '@/hooks/useNotifications';
import { useAuth } from '@/hooks/useAuth';
import { teamQueryKeys, getEffectiveTeamId } from '@/lib/queryKeys';
import { formatDateOnly } from '@/utils/dateFormat';
import { usePostsStore } from '@/stores/postsStore';
import { Notification, NotificationScope } from '@/types/notification';
import { getNotificationVisual, getNotificationHref, translateNotification } from '@/utils/notificationUtils';
import { useNotificationScope } from '@/hooks/useNotifications';
import { NotificationBrandChip } from './NotificationBrandChip';
import { Icon } from '@/components/ui/Icon';

/**
 * Hook to format relative time for notifications
 */
const useFormatRelativeTime = () => {
  const { t } = useTranslation('common');

  return (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffSeconds < 60) {
      return t('time_just_now');
    } else if (diffMinutes < 60) {
      return t('time_minutes_ago', { count: diffMinutes });
    } else if (diffHours < 24) {
      return t('time_hours_ago', { count: diffHours });
    } else if (diffDays < 7) {
      return t('time_days_ago', { count: diffDays });
    } else {
      return formatDateOnly(date);
    }
  };
};

/**
 * Single notification item
 */
interface NotificationItemProps {
  notification: Notification;
  onMarkAsRead: (id: string) => void;
  onClose: () => void;
  onRefreshPosts?: () => void;
  scope: NotificationScope;
}

const NotificationItem = ({ notification, onMarkAsRead, onClose, onRefreshPosts, scope }: NotificationItemProps) => {
  const { t } = useTranslation('common');
  const formatRelativeTime = useFormatRelativeTime();

  // Translate notification
  const translated = translateNotification(
    notification.type,
    notification.title,
    notification.message,
    { teamName: notification.teamName ?? undefined, ...notification.data },
    t
  );

  const handleClick = () => {
    // Mark as read (fire and forget - mutation handles its own state)
    if (!notification.isRead) {
      onMarkAsRead(notification.id);
    }
    // Refresh posts for Canva import notifications so user sees latest data
    if (notification.type.startsWith('CANVA_IMPORT_') && onRefreshPosts) {
      onRefreshPosts();
    }
    onClose();
  };

  const link = getNotificationHref(notification, scope);
  const { icon: notificationIcon, color: notificationColor } = getNotificationVisual(notification);

  return (
    <Link
      to={link}
      onClick={handleClick}
      className={`block px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 ${
        !notification.isRead ? 'bg-soft-mint' : ''
      }`}
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div
          className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center bg-gray-100 ${notificationColor}`}
        >
          <Icon name={notificationIcon} size="custom" className="text-[14px]" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">{translated.title}</p>
          <p className="text-sm text-gray-500 line-clamp-2">{translated.body}</p>
          {scope === 'portfolio' && notification.teamName && (
            <div className="mt-1 flex">
              <NotificationBrandChip name={notification.teamName} logo={notification.teamLogo} />
            </div>
          )}
          <p className="text-xs text-gray-400 mt-1">{formatRelativeTime(notification.createdAt)}</p>
        </div>

        {/* Unread indicator */}
        {!notification.isRead && (
          <div className="flex-shrink-0">
            <div className="w-2 h-2 rounded-full bg-primary-600"></div>
          </div>
        )}
      </div>
    </Link>
  );
};

/**
 * Notification Bell with Dropdown
 */
export const NotificationBell = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const teamId = getEffectiveTeamId(user);

  // Queries
  const scope = useNotificationScope();
  const { data: unreadCount = 0 } = useUnreadNotificationCount();
  const { data: notificationsData, isLoading } = useNotifications(1, 10, false);

  // Mutations
  const markAsReadMutation = useMarkNotificationAsRead();
  const markAllAsReadMutation = useMarkAllNotificationsAsRead();

  const notifications = notificationsData?.notifications || [];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !event.defaultPrevented) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  // Auto-mark all notifications as read when dropdown opens (Facebook-style)
  // Track the last time we marked as read to prevent rapid re-calls
  const lastMarkedAt = useRef<number>(0);

  useEffect(() => {
    // Only run when dropdown is open and there are unread notifications
    // Use unreadCount (from separate API) instead of filtering visible notifications
    // because unread notifications might be beyond page 1
    if (!isOpen || unreadCount === 0) return;

    // Debounce: don't call again if we called within the last 2 seconds
    const now = Date.now();
    if (now - lastMarkedAt.current < 2000) return;

    lastMarkedAt.current = now;
    markAllAsReadMutation.mutate();
  }, [isOpen, unreadCount, markAllAsReadMutation]);

  const handleMarkAsRead = (id: string) => {
    markAsReadMutation.mutate(id);
  };

  const handleMarkAllAsRead = () => {
    markAllAsReadMutation.mutate();
  };

  const handleRefreshPosts = () => {
    // Refresh posts in Zustand store (Creator page uses this, not React Query)
    usePostsStore.getState().fetchPosts();
    // Also invalidate React Query caches for other pages that might use them
    queryClient.invalidateQueries({ queryKey: teamQueryKeys.posts.all(teamId) });
    queryClient.invalidateQueries({ queryKey: teamQueryKeys.mediaLibrary.all(teamId) });
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative inline-flex items-center justify-center max-sm:min-w-[44px] max-sm:min-h-[44px] p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors focus:outline-none"
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        {/* Bell Icon (SVG) */}
        <svg
          style={{ width: 'clamp(18px, 2.5vh, 24px)', height: 'clamp(18px, 2.5vh, 24px)' }}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>

        {/* Badge */}
        {unreadCount > 0 && (
          <span className="absolute top-0.5 right-0.5 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 text-[10px] font-bold leading-none text-white bg-primary-700 rounded-full">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <>
          {/* Backdrop — mobile only */}
          <div
            className="fixed inset-0 bg-black/20 z-40 sm:hidden"
            onClick={() => setIsOpen(false)}
          />

          <div
            className="fixed left-4 right-4 top-[4.5rem] sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-96 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden z-50"
            role="menu"
            aria-orientation="vertical"
          >
            {/* Header */}
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between bg-gray-50">
              {/* @ts-ignore - react-i18next v16 type instantiation depth issue */}
              <h3 className="text-sm font-semibold text-gray-900">{t('notifications_title')}</h3>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <button
                    onClick={handleMarkAllAsRead}
                    disabled={markAllAsReadMutation.isPending}
                    className="max-sm:inline-flex max-sm:items-center max-sm:justify-center max-sm:min-h-[44px] max-sm:min-w-[44px] text-xs text-primary-600 hover:text-primary-700 disabled:opacity-50"
                  >
                    {/* @ts-ignore - react-i18next v16 type instantiation depth issue */}
                    {t('notifications_mark_all_as_read')}
                  </button>
                )}
                <button
                  onClick={() => setIsOpen(false)}
                  className="sm:hidden max-sm:inline-flex max-sm:items-center max-sm:justify-center max-sm:min-h-[44px] max-sm:min-w-[44px] p-1 hover:bg-gray-200 rounded-md transition-colors"
                  aria-label="Close"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Notification List */}
            <div className="max-h-[60vh] sm:max-h-96 overflow-y-auto">
              {isLoading ? (
                <div className="px-4 py-8 text-center text-gray-500">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-400 mx-auto mb-2"></div>
                  {/* @ts-ignore - react-i18next v16 type instantiation depth issue */}
                  {t('notifications_loading_more')}
                </div>
              ) : notifications.length === 0 ? (
                <div className="px-4 py-8 text-center text-gray-500">
                  <svg
                    className="w-12 h-12 mx-auto mb-2 text-gray-300"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                    />
                  </svg>
                  {/* @ts-ignore - react-i18next v16 type instantiation depth issue */}
                  <p className="text-sm">{t('notifications_empty_title')}</p>
                </div>
              ) : (
                notifications.map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onMarkAsRead={handleMarkAsRead}
                    onClose={() => setIsOpen(false)}
                    onRefreshPosts={handleRefreshPosts}
                    scope={scope}
                  />
                ))
              )}
            </div>

            {/* Footer */}
            <div className="px-4 py-3 border-t border-gray-200 bg-gray-50">
              <Link
                to="/notifications"
                onClick={() => setIsOpen(false)}
                className="block text-center text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                {/* @ts-ignore - react-i18next v16 type instantiation depth issue */}
                {t('notifications_view_all')}
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationBell;
